import axios, { isAxiosError } from "axios";
import { MOSIDA_API_BASE_URL } from "../config/apiUrls";

export const DEFAULT_KIOSK_TIMEOUT_MS = 45_000;
export const DEFAULT_QR_EXPIRY_MS = 120_000;
export const DEFAULT_LOGOUT_SESSION_MS = 45_000;

const kioskTimeoutUrl = `${MOSIDA_API_BASE_URL}/kiosk-timeout-setting`;

export type KioskTimeoutSettings = {
  milliseconds: number;
  qrExpiryMs: number;
  logoutSessionMs: number;
};

function parseMilliseconds(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return Math.round(value);
  }

  if (typeof value === "string" && value.trim()) {
    const parsed = parseInt(value, 10);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
  }

  return null;
}

function extractSettingsRecord(payload: unknown): Record<string, unknown> | null {
  if (!payload || typeof payload !== "object") return null;

  const root = payload as Record<string, unknown>;
  if (
    parseMilliseconds(root.milliseconds) ||
    parseMilliseconds(root.qr_expiry) ||
    parseMilliseconds(root.logout_session)
  ) {
    return root;
  }

  const data = root.data;
  if (!data || typeof data !== "object") return null;

  const record = data as Record<string, unknown>;
  if (
    parseMilliseconds(record.milliseconds) ||
    parseMilliseconds(record.qr_expiry) ||
    parseMilliseconds(record.logout_session)
  ) {
    return record;
  }

  const nested = record.data;
  if (nested && typeof nested === "object") {
    return nested as Record<string, unknown>;
  }

  return null;
}

function extractMilliseconds(payload: unknown): number | null {
  const record = extractSettingsRecord(payload);
  if (!record) return null;
  return parseMilliseconds(record.milliseconds);
}

function extractQrExpiryMs(payload: unknown): number | null {
  const record = extractSettingsRecord(payload);
  if (!record) return null;
  return parseMilliseconds(record.qr_expiry);
}

function extractLogoutSessionMs(payload: unknown): number | null {
  const record = extractSettingsRecord(payload);
  if (!record) return null;
  return parseMilliseconds(record.logout_session);
}

export function parseKioskTimeoutSettings(payload: unknown): KioskTimeoutSettings {
  return {
    milliseconds: extractMilliseconds(payload) ?? DEFAULT_KIOSK_TIMEOUT_MS,
    qrExpiryMs: extractQrExpiryMs(payload) ?? DEFAULT_QR_EXPIRY_MS,
    logoutSessionMs: extractLogoutSessionMs(payload) ?? DEFAULT_LOGOUT_SESSION_MS,
  };
}

export async function fetchKioskTimeoutSettings(): Promise<KioskTimeoutSettings> {
  try {
    const response = await axios.get(kioskTimeoutUrl, {
      headers: { accept: "*/*" },
    });

    return parseKioskTimeoutSettings(response.data);
  } catch (error) {
    if (isAxiosError(error)) {
      console.warn("[kioskTimeoutService] Failed to load timeout:", error.message);
    }
    return {
      milliseconds: DEFAULT_KIOSK_TIMEOUT_MS,
      qrExpiryMs: DEFAULT_QR_EXPIRY_MS,
      logoutSessionMs: DEFAULT_LOGOUT_SESSION_MS,
    };
  }
}

export async function fetchKioskTimeoutMs(): Promise<number> {
  const settings = await fetchKioskTimeoutSettings();
  return settings.milliseconds;
}
